import c from 'classnames';
import InlineEdit from 'core/components/InlineEdit';
import useToggle from 'core/hooks/useToggle';
import { button } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { numberSeparateThousands } from 'core/utils';
import { formatToLocalDayMonthYearDot } from 'core/utils/date';
import React from 'react';
import { validatePositiveNumber } from '../financial-calculators/const';
import { IWeekInvestment, sliceWeekInvestments } from './helpers';

type IService52WeeksMobileTableProps = {
    weekInvestments: IWeekInvestment[];
    onChangeWeek(val: string, week: IWeekInvestment): void;
} & WithStyles<typeof styles>;

const Service52WeeksMobileTable: React.FC<IService52WeeksMobileTableProps> = ({
    classes,
    weekInvestments,
    onChangeWeek,
}) => {
    const [toggle, { switchToggle }] = useToggle();
    const tableData = toggle ? weekInvestments : sliceWeekInvestments(weekInvestments, 3);

    return (
        <>
            {tableData.map(week => (
                <table
                    key={week.weekNumber}
                    className={c(classes.weekTable, { [classes.currentWeek]: week.isCurrentWeek })}
                >
                    <tbody>
                        <tr className={classes.weekRow}>
                            <td colSpan={3}>
                                Неделя {formatToLocalDayMonthYearDot(week.from)} -{' '}
                                {formatToLocalDayMonthYearDot(week.to)}
                            </td>
                        </tr>
                        <tr className={classes.headerRow}>
                            <td></td>
                            <td>Отложить</td>
                            <td>Сумма</td>
                        </tr>
                        <tr>
                            <td>План</td>
                            <td>{numberSeparateThousands(week.plan.investment)}</td>
                            <td>{numberSeparateThousands(week.plan.sum)}</td>
                        </tr>
                        <tr>
                            <td>Факт</td>
                            <td>
                                {week.fact.investment !== null ? (
                                    <InlineEdit
                                        text={week.fact.investment.toString()}
                                        formatter={numberSeparateThousands}
                                        change={val => onChangeWeek(val, week)}
                                        validate={validatePositiveNumber}
                                        className={classes.staticInlineEdit}
                                        activeClassName={classes.editingInlineEdit}
                                        inputMode="decimal"
                                    />
                                ) : (
                                    '-'
                                )}
                            </td>
                            <td>{week.fact.sum !== null ? numberSeparateThousands(week.fact.sum) : '-'}</td>
                        </tr>
                    </tbody>
                </table>
            ))}
            <button onClick={switchToggle} className={classes.showAll}>
                {toggle ? 'Скрыть' : 'Показать'} все
            </button>
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        weekTable: {
            width: 100 + '%',
            border: `1px solid ${theme.colors.contrastBgMedium}`,
            paddingBottom: 15,

            '& td, th': {
                fontSize: 16,
                textAlign: 'center',
                padding: 5,
            },

            '& tr$headerRow > td': {
                fontSize: 12,
                fontWeight: 'bold',
            },
        },
        currentWeek: {
            borderColor: theme.colors.accent,
        },
        weekRow: {
            fontWeight: 'bold',
            color: '#808080',

            '& > td': {
                padding: '20px 0',
            },
        },
        headerRow: {
            color: '#808080',
        },
        staticInlineEdit: {
            border: `1px dashed transparent`,
            borderBottomColor: theme.colors.accent,
            cursor: 'pointer',

            '&:hover, &:focus': {
                outline: 'none',
                borderColor: theme.colors.accent,
            },
        },
        editingInlineEdit: {
            width: 60,
            outline: 'none',
        },
        showAll: {
            ...button(theme),
            fontWeight: 'bold',
            border: `1px solid #DADADA`,
            alignSelf: 'end',
            fontSize: 0.9 + 'rem',
            width: 100 + '%',
            display: 'block',

            '&:hover, &:focus': {
                color: 'white',
                backgroundColor: '#1E252F',
            },
        },
    });

export default injectSheet(styles)(Service52WeeksMobileTable);
